import * as THREE from 'three/webgpu';
import { sampleGround } from '../terrain/heightfield.js';
import { WHEELS } from '../vehicle/jeep.js';

const MAX_PARTICLES = 160;

export class DustParticles {
  constructor(scene) {
    this.maxCount = MAX_PARTICLES;
    this.particles = [];
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.particles.push({
        active: false,
        x: 0, y: 0, z: 0,
        vx: 0, vy: 0, vz: 0,
        life: 0,
        maxLife: 0.8,
        scale: 0.2
      });
    }

    // Geometry: soft low-poly puff (Dodecahedron)
    const geom = new THREE.DodecahedronGeometry(0.5, 0);
    const mat = new THREE.MeshStandardNodeMaterial({
      color: 0xc4b59d, // Warm desert sand / dust
      roughness: 0.95,
      metalness: 0.0,
      transparent: true,
      opacity: 0.55,
      depthWrite: false
    });

    this.mesh = new THREE.InstancedMesh(geom, mat, MAX_PARTICLES);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    scene.add(this.mesh);

    this.dummy = new THREE.Object3D();
    this.emitTimer = 0;
  }

  update(dt, jeep, input) {
    // 1. Update existing particles
    let activeAny = false;
    for (let i = 0; i < this.maxCount; i++) {
      const p = this.particles[i];
      if (!p.active) {
        this.dummy.position.set(0, -999, 0);
        this.dummy.scale.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(i, this.dummy.matrix);
        continue;
      }

      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        this.dummy.position.set(0, -999, 0);
        this.dummy.scale.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(i, this.dummy.matrix);
        continue;
      }

      activeAny = true;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.vy *= 0.96;
      p.vx *= 0.96;
      p.vz *= 0.96;

      // Particle expands as it disperses
      const progress = 1.0 - (p.life / p.maxLife);
      const curScale = p.scale * (1.0 + progress * 2.8) * Math.sin((1.0 - progress) * Math.PI);

      this.dummy.position.set(p.x, p.y, p.z);
      this.dummy.scale.set(curScale, curScale * 0.8, curScale);
      this.dummy.rotation.set(progress * 1.5, progress * 2.0, 0);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.mesh.instanceMatrix.needsUpdate = true;

    // 2. Emit new dust
    const absSpeed = Math.abs(jeep.speed);
    const shouldEmit = jeep.onGround && (absSpeed > 2.5 || (input.throttle && absSpeed < 1.0));

    if (shouldEmit) {
      this.emitTimer += dt;
      const interval = jeep.slip > 0.25 || jeep.boosting ? 0.025 : 0.055;

      if (this.emitTimer >= interval) {
        this.emitTimer = 0;
        const sinYaw = Math.sin(jeep.yaw);
        const cosYaw = Math.cos(jeep.yaw);

        // Rear wheels emit most dust; front wheels emit during drift
        const wheelsToEmit = jeep.slip > 0.3 ? [0, 1, 2, 3] : [2, 3];

        for (const wIdx of wheelsToEmit) {
          const w = WHEELS[wIdx];
          const ox = w.x * sinYaw + w.z * cosYaw;
          const oz = w.x * cosYaw - w.z * sinYaw;
          const wx = jeep.pos.x + ox;
          const wz = jeep.pos.z + oz;
          const g = sampleGround(wx, wz);

          // Find free slot
          for (let i = 0; i < this.maxCount; i++) {
            const p = this.particles[i];
            if (!p.active) {
              p.active = true;
              p.x = wx + (Math.random() - 0.5) * 0.4;
              p.y = g.height + 0.15;
              p.z = wz + (Math.random() - 0.5) * 0.4;
              p.vx = -sinYaw * jeep.speed * 0.15 + (Math.random() - 0.5) * 1.2;
              p.vy = 0.8 + Math.random() * 1.4;
              p.vz = -cosYaw * jeep.speed * 0.15 + (Math.random() - 0.5) * 1.2;
              p.maxLife = 0.6 + Math.random() * 0.4;
              p.life = p.maxLife;
              p.scale = 0.3 + (jeep.slip + (jeep.boosting ? 0.5 : 0)) * 0.35;
              break;
            }
          }
        }
      }
    }
  }
}
